import { getDocument } from '@atlaskit/browser-apis';
import { aiQuickPromptMessages } from '@atlaskit/editor-common/ai-messages';
import type { PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import type { EligibleRange } from './types';

// eslint-disable-next-line require-unicode-regexp
const CREATE_MESSAGE_NODES_CODE_REGEX = /<code>(.*)<\/code>/;

function createMessageNodes({ doc, message }: { doc: Document; message: string }) {
	const node = doc.createElement('div');
	node.className = 'ai-quick-prompt-message';
	const parts = message.split(CREATE_MESSAGE_NODES_CODE_REGEX);
	parts.forEach((part) => {
		if (!part) return;
		const span = doc.createElement('span');
		span.textContent = part;
		if (message.includes(`<code>${part}</code>`)) {
			span.className = 'ai-quick-prompt-code';
		}
		node.appendChild(span);
	});
	return node;
}

function createCaretNode({ doc }: { doc: Document }) {
	const caret = doc.createElement('span');
	caret.className = 'ai-quick-prompt-caret';
	return caret;
}

function createMessageContainer({ doc }: { doc: Document }) {
	const container = doc.createElement('div');
	container.setAttribute('data-testid', 'ai-quick-prompt-placeholder-decoration');
	container.className =
		'placeholder-decoration placeholder-decoration-inline ai-quick-prompt-placeholder-decoration placeholder-decoration-fade-in';
	return container;
}

/**
 * Create a DecorationSet containing the nudge widget at the end
 * of the eligible paragraph node.
 *
 * @param state - Current editor state
 * @param range - The eligible node range { from, to }
 * @param intl - The intl object for formatting messages
 * @returns A DecorationSet with the nudge widget
 */
export function createNudgeDecorationSet({
	getIntl,
	range,
	state,
}: {
	getIntl: PMPluginFactoryParams['getIntl'];
	range: EligibleRange;
	state: EditorState;
}): DecorationSet {
	const widget = () => {
		const doc = getDocument();
		if (!doc) {
			throw new Error('Document not found');
		}

		const container = createMessageContainer({ doc });
		const intl = getIntl();
		const message = intl.formatMessage(aiQuickPromptMessages.nudgeText);
		container.appendChild(createCaretNode({ doc }));
		container.appendChild(createMessageNodes({ doc, message }));
		return container;
	};

	return DecorationSet.create(state.doc, [
		Decoration.widget(range.to, widget, {
			side: 1, // After the content
			key: 'ai-quick-prompt-nudge',
		}),
	]);
}
